import { Component, OnDestroy, OnInit } from '@angular/core';
import { ScreenWidthListenerService } from '../../../services/screen-width-detector/screen-width-listener.service';
import { MfaSelectCardViewModel } from './models/mfa-select-card-view-model';
import { MfaType } from '../../../models/enums/mfa-type';
import { TranslateService } from '../../../services/translate/translate.service';
import { MfaLoginService } from '../../../services/authentication/mfa-login.service';
import { MfaLoginDataWithToken } from '../../../models/User/mfa/mfa-tokens/mfa-login-data';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { SessionStatusBroadcastService } from '../../../services/authorization/session-status-broadcast.service';
import { BroadcastMessage } from '../../../models/broadcast/broadcast-message';
import { BroadcastMessageType } from '../../../models/broadcast/broadcast-message-type';

@Component({
	selector: 'eif-mfa-select',
	templateUrl: './mfa-select.component.html',
	styleUrls: ['./mfa-select.component.scss'],
	standalone: false,
})
export class MfaSelectComponent implements OnInit, OnDestroy {
	private mfaData: MfaLoginDataWithToken;
	protected mfaSelectOptions: MfaSelectCardViewModel[] = [];
	protected mfaSelectOptionsLoaded: Promise<boolean>;
	private sessionKilledSubscription: Subscription;

	constructor(
		protected screenWidthListener: ScreenWidthListenerService,
		private translateService: TranslateService,
		private route: ActivatedRoute,
		private mfaLoginService: MfaLoginService,
		private sessionStatusBroadcastService: SessionStatusBroadcastService
	) {}

	ngOnInit(): void {
		this.mfaLoginService.getMfaLoginDataFromRoute(this.route).then((mfaData: MfaLoginDataWithToken) => {
			this.mfaData = mfaData;
			if (this.mfaData?.MfaToken) {
				this.subscribeSessionKill();
				this.loadMfaSelectOptions();
			} else {
				this.goBack();
			}
		});
	}

	ngOnDestroy(): void {
		this.unsubscribeSessionKill();
	}

	private subscribeSessionKill(): void {
		this.sessionKilledSubscription = this.sessionStatusBroadcastService
			.messagesOfType(BroadcastMessageType.SessionKilled)
			.subscribe((msg: BroadcastMessage) => {
				if (msg.type == BroadcastMessageType.SessionKilled) {
					this.unsubscribeSessionKill();
					this.mfaLoginService.cancelMfaLogin();
				}
			});
	}

	private unsubscribeSessionKill(): void {
		if (!!this.sessionKilledSubscription) {
			this.sessionKilledSubscription.unsubscribe();
		}
	}

	private loadMfaSelectOptions(): void {
		this.mfaSelectOptions = this.mfaData.MfaLoginData.UserMfaAuthenticators.map((userMfa) => {
			const option = new MfaSelectCardViewModel();
			option.UserMfa = userMfa;

			if (option.UserMfa.MfaTypeName === MfaType.OTP) {
				option.MfaInfoIconUrl = 'assets/images/icon_dark_mfa_authenticator_app.svg';
				option.MfaTypeTitle = this.translateService.translate('mfa-select.mfaAuthenticatorTitleOtp', 'client');
				option.MfaTypeDescription = this.translateService.translate('mfa-select.mfaAuthenticatorDescriptionOtp', 'client');
			} else if (option.UserMfa.MfaTypeName === MfaType.EMAIL) {
				option.MfaInfoIconUrl = 'assets/images/icon_dark_mfa_authenticator_email.svg';
				option.MfaTypeTitle = this.translateService.translate('mfa-select.mfaAuthenticatorTitleEmail', 'client');
				option.MfaTypeDescription = this.translateService.translate('mfa-select.mfaAuthenticatorDescriptionEmail', 'client');
			}

			return option;
		});

		this.mfaSelectOptionsLoaded = Promise.resolve(true);
	}

	protected goBack(): void {
		this.mfaLoginService.cancelMfaLogin();
	}

	protected navigateToMfaPage(selectedMfaOption: MfaSelectCardViewModel): void {
		this.mfaLoginService.goToNextMfaPage(this.mfaData.MfaLoginData, selectedMfaOption.UserMfa);
	}
}
